import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "./tokens"

/**
 * Base element styles for the Modern skin, ported from the prototype's
 * `index.css`. Delivered as a JSS `@global` sheet mounted by <ModernShell/>
 * only — JSS detaches it on unmount, so flipping back to Classic removes every
 * rule below in the same commit and Classic can never inherit them.
 */
export default makeStyles((theme: ModernTheme) => ({
    "@global": {
        "html, body, #root": {
            height: "100%",
        },
        body: {
            font: `400 13px/1.5 ${theme.modern.sans}`,
            color: theme.modern.ink,
            backgroundColor: theme.modern.bg,
            // MUI's CssBaseline is not used by this app; Classic relies on
            // App.css, which we must override while Modern is mounted
            margin: 0,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontSynthesis: "none",
            fontVariantNumeric: "tabular-nums lining-nums",
        },
        "*": {
            boxSizing: "border-box",
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.modern.line} transparent`,
        },
        /**
         * Every control in Classic is a MUI <Button/>, which uppercases its own
         * label in CSS — so the app has always spoken in caps, and the English
         * strings behind those labels were written lowercase because nothing
         * ever showed them raw. Modern renders plain <button> elements, which
         * uppercase nothing, so the two skins disagreed on the casing of every
         * single control and Modern disagreed with itself ("approve" beside
         * "Approve & next").
         *
         * Declared once here rather than on fifteen button classes, and as an
         * element selector so any component class can opt out on specificity
         * alone — <ModernStatCards/> is the one that does.
         */
        button: {
            textTransform: "uppercase",
        },
        "::-webkit-scrollbar": {width: 10, height: 10},
        "::-webkit-scrollbar-track": {background: "transparent"},
        "::-webkit-scrollbar-thumb": {
            background: theme.modern.line,
            borderRadius: 99,
            border: "2.5px solid transparent",
            backgroundClip: "content-box",
        },
        "::-webkit-scrollbar-thumb:hover": {
            background: theme.modern.ink3,
            border: "2.5px solid transparent",
            backgroundClip: "content-box",
        },
        "::-webkit-scrollbar-corner": {background: "transparent"},
        ":focus-visible": {
            outline: `2px solid ${theme.modern.accent}`,
            outlineOffset: 1,
            borderRadius: 4,
        },
        "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
                transition: "none !important",
                animation: "none !important",
            },
        },
    },
}))
